import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Package } from 'lucide-react'
import { MULTI_PRODUCT_INDIGO } from './RemakeTag'

/* ==================================================================
 * VideoProductChip — which product a submitted video is actually for
 * ==================================================================
 * A campaign used to hold exactly one product, so "which product is this
 * video about?" answered itself: there was only one. On a multi-product
 * campaign (2026-09-11) a 10-video hire can be ten videos on one product or
 * two each across five, and nothing on the brand's side said which — every
 * video row rendered identically.
 *
 * The answer is already stored per video, written by the creator-app's
 * enrich-video-submission function from what TikTok reports the video tagged:
 *
 *   submitted_product_id / _name  what the video is actually tagged with
 *   matched_product_id   / _name  the campaign product that resolved to, if any
 *   product_match                 true / false / null (no product configured)
 *
 * Indigo, because that is already what "this is about products" means
 * everywhere else on this surface (MULTI_PRODUCT_GRADIENT, MultiProductTag) —
 * see the colour-semantics note at the top of RemakeTag.tsx. Deliberately a
 * quiet outline rather than the filled gradient the campaign badge uses: this
 * appears once per row in a long table, and a filled pill repeated forty times
 * reads as noise.
 * ================================================================== */

/** The product half of a cr_video_submissions row. */
export interface VideoProduct {
  submitted_product_id?: string | null
  submitted_product_name?: string | null
  matched_product_id?: string | null
  matched_product_name?: string | null
  product_match?: boolean | null
}

/** Columns to add to a cr_video_submissions `select()` for the product chip —
 *  mirrors VIDEO_FLAG_COLUMNS in VideoFlagModal.tsx, same reason: one string,
 *  spliced into each page's select, so the five column names are written down
 *  once and can't drift between the screens that read them. */
export const VIDEO_PRODUCT_COLUMNS =
  'submitted_product_id, submitted_product_name, matched_product_id, matched_product_name, product_match'

/**
 * What to call the product this video is for.
 *
 * Prefers the MATCHED product — that's the campaign's own product, named the
 * way the brand named it — and falls back to whatever TikTok reported was
 * tagged, which is all a video submitted before the matched_* columns existed
 * (or one tagged with something off-campaign) ever has. Returns null when
 * there is nothing on record, so callers can render nothing rather than an
 * "Unknown" chip on every legacy row.
 */
export function videoProductLabel(v: VideoProduct | null | undefined): string | null {
  const name = v?.matched_product_name?.trim() || v?.submitted_product_name?.trim()
  if (name) return name
  // No name anywhere, but an id is still better than silence — it's what the
  // brand would search their TikTok Shop for.
  const id = v?.matched_product_id?.trim() || v?.submitted_product_id?.trim()
  return id || null
}

/** Cuts a label down to a fixed, predictable width so a long product name
 *  can never stretch a table row or a card — every other truncation on this
 *  page relies on CSS ellipsis inside a maxWidth, which shifts with font
 *  rendering and container width. A character cap doesn't. 14 chars: long
 *  enough to read as a real word or two ("SuperCalm Powd…"), short enough that
 *  even three or four chips in a row stay put. */
const CHIP_LABEL_MAX = 14

function truncateLabel(label: string, max: number = CHIP_LABEL_MAX): string {
  return label.length > max ? `${label.slice(0, max).trimEnd()}…` : label
}

/**
 * Small hover card showing one line of text, portal-rendered with `position:
 * fixed` so it is never clipped by an ancestor `overflow: hidden` (a rounded
 * table container, a scrolling strip) — same technique as InfoTooltip.tsx and
 * FlagTooltip in VideoFlagModal.tsx. Deliberately NOT the native `title`
 * attribute: title is unstyled, slow to appear, and unreadable on dark
 * surfaces — this app already has three hand-rolled tooltips for exactly that
 * reason, and every one of them is this same shape.
 */
function ProductNameTooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [show, setShow] = useState(false)

  const open = () => { if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect()); setShow(true) }
  const close = () => setShow(false)

  useEffect(() => {
    if (!show) return
    const reposition = () => { if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect()) }
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [show])

  const MARGIN = 8
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const openBelow = rect != null && rect.top < 60
  const left = rect ? Math.max(MARGIN, Math.min(rect.left, vw - MARGIN)) : 0
  const top = rect ? (openBelow ? rect.bottom + MARGIN : rect.top - MARGIN) : 0

  return (
    <span
      ref={triggerRef}
      onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}
      tabIndex={0}
      style={{ outline: 'none', minWidth: 0, display: 'inline-flex' }}
    >
      {children}
      {show && rect && createPortal(
        <span
          role="tooltip"
          style={{
            position: 'fixed', left, top, zIndex: 9999, pointerEvents: 'none',
            transform: openBelow ? 'none' : 'translateY(-100%)',
            maxWidth: 260,
            padding: '6px 10px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.4,
            letterSpacing: 'normal',
            textTransform: 'none',
            whiteSpace: 'normal',
            color: '#fff',
            background: '#15151b',
            borderRadius: 8,
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1), 0 10px 26px -8px rgba(0,0,0,0.7)',
          }}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  )
}

/**
 * One video's product, as a chip.
 *
 * Renders nothing when the video has no product on record — a legacy row, or
 * a campaign with no product configured at all. That's deliberate: a column of
 * "—" placeholders on a single-product brand's table is worse than an empty
 * cell, and this component is mounted on tables that serve both.
 *
 * The label shown is capped at CHIP_LABEL_MAX characters; the full name is
 * always available in the custom hover tooltip (see ProductNameTooltip) — a
 * long product name can no longer stretch a table row.
 */
export function VideoProductChip({ video, compact = false }: {
  video: VideoProduct | null | undefined
  /** Tighter padding for dense rows (the expanded sub-rows on /hires). */
  compact?: boolean
}) {
  const label = videoProductLabel(video)
  if (!label) return null

  // A video tagged with something that isn't on this campaign is the one case
  // worth colouring differently — it's the same fact the admin's "Not Matched"
  // stat counts, and a brand looking at their own table should see it too.
  const offCampaign = video?.product_match === false
  const shown = truncateLabel(label)

  return (
    <ProductNameTooltip text={offCampaign ? `Tagged "${label}", which isn't a product on this campaign` : label}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          maxWidth: '100%',
          fontSize: compact ? 10 : 11,
          fontWeight: 600,
          padding: compact ? '2px 7px' : '3px 9px',
          borderRadius: 999,
          background: offCampaign ? 'var(--danger-subtle)' : 'rgba(129, 140, 248, 0.12)',
          color: offCampaign ? 'var(--danger)' : MULTI_PRODUCT_INDIGO,
          border: `1px solid ${offCampaign ? 'transparent' : 'rgba(129, 140, 248, 0.30)'}`,
          flexShrink: 0,
          cursor: 'default',
        }}
      >
        <Package size={compact ? 9 : 10} strokeWidth={2.5} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {shown}
        </span>
      </span>
    </ProductNameTooltip>
  )
}
